import { useState } from 'react';
import type { ConversionStage } from '@chefty/shared-types';
import { adminApi } from '../services/admin';
import { useAsync } from '../hooks/useAsync';
import { fmtInt, fmtPct } from '../lib/format';
import { EmptyState, Eyebrow, Loading, Panel } from './ui';

const WINDOWS = [7, 30, 90] as const;

const STAGE_LABEL: Record<ConversionStage, string> = {
  new_users: 'New users',
  activated: 'Completed an import',
  saved_recipe: 'Saved a recipe',
  paywall_shown: 'Saw the paywall',
  signed_up: 'Created an account',
  subscribed: 'Subscribed',
};

/**
 * Signup-cohort conversion from our own Postgres. Every bar is a share of the
 * window's new users — stages are counted independently, so they are not step
 * ratios (see the contract in shared-types).
 */
export function ConversionPanel() {
  const [days, setDays] = useState<(typeof WINDOWS)[number]>(30);
  const conversion = useAsync(() => adminApi.conversion(days), [days]);
  const data = conversion.data;
  const cohort = data?.stages.find((s) => s.stage === 'new_users')?.users ?? 0;

  return (
    <Panel className="rise">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Eyebrow>Conversion</Eyebrow>
          <h2 className="font-display text-lg text-cream-50">
            Users who joined in the last {days} days
          </h2>
        </div>
        <div className="flex gap-2">
          {WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setDays(w)}
              aria-pressed={w === days}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                w === days
                  ? 'border-saffron-500/50 bg-saffron-500/10 text-saffron-300'
                  : 'border-cream-600/15 text-cream-400 hover:border-cream-600/30 hover:text-cream-200'
              }`}
            >
              {w}d
            </button>
          ))}
        </div>
      </div>

      {conversion.loading && !data ? (
        <Loading />
      ) : conversion.error ? (
        <EmptyState title="Couldn’t load conversion" hint={conversion.error} />
      ) : !data || cohort === 0 ? (
        <EmptyState title="No new users in this window" />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <ul className="space-y-3 lg:col-span-2">
            {data.stages.map(({ stage, users }) => {
              const share = users / cohort;
              return (
                <li key={stage}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-cream-200">{STAGE_LABEL[stage]}</span>
                    <span className="tnum text-cream-400">
                      {fmtInt(users)}
                      <span className="ml-3 inline-block w-14 text-right text-cream-50">
                        {fmtPct(share)}
                      </span>
                    </span>
                  </div>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-cream-600/10">
                    <div
                      className={`h-full rounded-full ${stage === 'subscribed' ? 'bg-saffron-400' : 'bg-teal-400'}`}
                      style={{ width: `${Math.min(share, 1) * 100}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="rounded-xl border border-cream-600/15 p-4">
            <Eyebrow>Paywall → paid</Eyebrow>
            <div className="tnum mt-2 font-display text-3xl font-bold leading-none text-cream-50">
              {data.paywallToPaid.shown
                ? fmtPct(data.paywallToPaid.paid / data.paywallToPaid.shown)
                : '—'}
            </div>
            <p className="mt-2 text-sm text-cream-400">
              {fmtInt(data.paywallToPaid.paid)} of {fmtInt(data.paywallToPaid.shown)} who saw the
              paywall subscribed.
            </p>
          </div>
        </div>
      )}
    </Panel>
  );
}
